import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Brain, Users, MessageCircle, BookOpen, Shield, Sparkles, Award, Target, Zap, TrendingUp, Star } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
import { ModuleModal } from '../components/ModuleModal';
export function HomePage() {
  const navigate = useNavigate();
  const {
    progress,
    incrementStreak
  } = useProgress();
  const [selectedModule, setSelectedModule] = useState<{
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
  } | null>(null);
  useEffect(() => {
    incrementStreak();
  }, []);
  const userName = progress.preferences.name || 'Friend';
  const modules = [{
    id: 'emotional-intelligence',
    title: 'Emotional Intelligence',
    description: 'Master your feelings and build emotional strength',
    icon: <Brain className="w-8 h-8 text-white" data-id="element-398" />,
    color: 'from-blue-500 to-indigo-500'
  }, {
    id: 'body-positivity',
    title: 'Body Positivity',
    description: 'Love your body and build confidence',
    icon: <Heart className="w-8 h-8 text-white" data-id="element-399" />,
    color: 'from-teal-500 to-cyan-500'
  }, {
    id: 'peer-relationships',
    title: 'Healthy Relationships',
    description: 'Navigate friendships and romantic relationships',
    icon: <Users className="w-8 h-8 text-white" data-id="element-400" />,
    color: 'from-indigo-500 to-blue-500'
  }, {
    id: 'communication',
    title: 'Communication Skills',
    description: 'Express yourself clearly and confidently',
    icon: <MessageCircle className="w-8 h-8 text-white" data-id="element-401" />,
    color: 'from-green-500 to-teal-500'
  }, {
    id: 'online-safety',
    title: 'Online Safety',
    description: 'Stay safe in the digital world',
    icon: <Shield className="w-8 h-8 text-white" data-id="element-402" />,
    color: 'from-orange-500 to-amber-500'
  }, {
    id: 'consent',
    title: 'Consent & Boundaries',
    description: 'Understand consent and respect boundaries',
    icon: <Target className="w-8 h-8 text-white" data-id="element-403" />,
    color: 'from-indigo-600 to-blue-600'
  }];
  const quickActions = [{
    id: 'stories',
    title: 'Interactive Stories',
    icon: <BookOpen className="w-6 h-6" data-id="element-404" />,
    color: 'from-purple-500 to-pink-500',
    gradient: 'from-purple-500/10 to-pink-500/10',
    onClick: () => navigate('/stories')
  }, {
    id: 'quizzes',
    title: 'Fun Quizzes',
    icon: <Award className="w-6 h-6" data-id="element-405" />,
    color: 'from-green-500 to-teal-500',
    gradient: 'from-green-500/10 to-teal-500/10',
    onClick: () => navigate('/quizzes')
  }, {
    id: 'chatbot',
    title: 'Ask Anything',
    icon: <MessageCircle className="w-6 h-6" data-id="element-406" />,
    color: 'from-blue-500 to-cyan-500',
    gradient: 'from-blue-500/10 to-cyan-500/10',
    onClick: () => navigate('/chatbot')
  }, {
    id: 'profile',
    title: 'Your Profile',
    icon: <Star className="w-6 h-6" data-id="element-407" />,
    color: 'from-orange-500 to-amber-500',
    gradient: 'from-orange-500/10 to-amber-500/10',
    onClick: () => navigate('/profile')
  }];
  return <>
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-blue-950 dark:to-indigo-950 pb-20 md:pb-8" data-id="element-408">
        {/* Hero Section */}
        <div className="relative overflow-hidden pattern-dots" data-id="element-409">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5" data-id="element-410" />
          <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-12 px-4" data-id="element-411">
            <div className="max-w-6xl mx-auto" data-id="element-412">
              <div className="flex items-center justify-between mb-6" data-id="element-413">
                <div className="flex-1" data-id="element-414">
                  <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center space-x-3 animate-fade-in" data-id="element-415">
                    <Sparkles className="w-8 h-8 animate-pulse" data-id="element-416" />
                    <span data-id="element-417">Welcome back, {userName}!</span>
                  </h1>
                  <p className="text-lg md:text-xl text-white/90" data-id="element-418">
                    Ready to continue your learning journey? 🌟
                  </p>
                </div>
              </div>
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3 md:gap-4" data-id="element-419">
                <div className="glass-effect glass-effect-dark rounded-2xl p-4 border border-white/20 card-hover" data-id="element-420">
                  <div className="flex items-center justify-between mb-2" data-id="element-421">
                    <Zap className="w-5 h-5 text-yellow-300" data-id="element-422" />
                    <div className="text-2xl md:text-3xl font-bold" data-id="element-423">
                      {progress.points}
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-white/80" data-id="element-424">
                    Total Points
                  </div>
                </div>
                <div className="glass-effect glass-effect-dark rounded-2xl p-4 border border-white/20 card-hover" data-id="element-425">
                  <div className="flex items-center justify-between mb-2" data-id="element-426">
                    <TrendingUp className="w-5 h-5 text-orange-300" data-id="element-427" />
                    <div className="text-2xl md:text-3xl font-bold" data-id="element-428">
                      {progress.streak}
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-white/80" data-id="element-429">
                    Day Streak 🔥
                  </div>
                </div>
                <div className="glass-effect glass-effect-dark rounded-2xl p-4 border border-white/20 card-hover" data-id="element-430">
                  <div className="flex items-center justify-between mb-2" data-id="element-431">
                    <Award className="w-5 h-5 text-purple-300" data-id="element-432" />
                    <div className="text-2xl md:text-3xl font-bold" data-id="element-433">
                      {progress.badges.filter(b => b.earned).length}
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-white/80" data-id="element-434">
                    Badges Earned
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-435">
          {/* Quick Actions */}
          <div className="mb-12 animate-fade-in" data-id="element-436">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2" data-id="element-437">
              <Zap className="w-6 h-6 text-blue-500" data-id="element-438" />
              <span data-id="element-439">Quick Actions</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-440">
              {quickActions.map(action => <button key={action.id} onClick={action.onClick} className="group relative bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border border-gray-100 dark:border-slate-700 overflow-hidden" data-id="element-441">
                  <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-100 transition-opacity`} data-id="element-442" />
                  <div className="relative" data-id="element-443">
                    <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`} data-id="element-444">
                      {action.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white group-hover:gradient-text group-hover:${action.color} transition-all" data-id="element-445">
                      {action.title}
                    </h3>
                  </div>
                </button>)}
            </div>
          </div>
          {/* Learning Modules */}
          <div className="animate-fade-in" data-id="element-446">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2" data-id="element-447">
              <BookOpen className="w-6 h-6 text-indigo-500" data-id="element-448" />
              <span data-id="element-449">Explore Learning Modules</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-450">
              Click any module to explore stories, quizzes, and resources
              tailored for you!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-451">
              {modules.map((module, index) => <button key={module.id} onClick={() => setSelectedModule(module)} className="group relative bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-left hover:-translate-y-2 border border-gray-100 dark:border-slate-700 overflow-hidden" style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-452">
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.color} opacity-0 group-hover:opacity-5 transition-opacity`} data-id="element-453" />
                  <div className="relative" data-id="element-454">
                    <div className={`w-16 h-16 bg-gradient-to-br ${module.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`} data-id="element-455">
                      {module.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:gradient-text group-hover:${module.color} transition-colors" data-id="element-456">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm" data-id="element-457">
                      {module.description}
                    </p>
                    <div className="flex items-center text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-2 transition-transform" data-id="element-458">
                      Explore <span className="ml-2" data-id="element-459">→</span>
                    </div>
                  </div>
                </button>)}
            </div>
          </div>
          {/* Motivational Message */}
          <div className="mt-12 glass-effect glass-effect-dark border border-blue-200 dark:border-blue-800 rounded-2xl p-8 animate-fade-in" data-id="element-460">
            <div className="flex items-start space-x-4" data-id="element-461">
              <div className="text-4xl animate-bounce" data-id="element-462">💪</div>
              <div data-id="element-463">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-464">
                  You're Doing Amazing, {userName}!
                </h3>
                <p className="text-gray-700 dark:text-gray-300" data-id="element-465">
                  Every step you take in learning about yourself and staying
                  safe is a step toward becoming your best self. Keep exploring,
                  keep growing, and remember - you've got this! 🌟
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Module Modal */}
      {selectedModule && <ModuleModal isOpen={!!selectedModule} onClose={() => setSelectedModule(null)} moduleId={selectedModule.id} title={selectedModule.title} description={selectedModule.description} icon={selectedModule.icon} color={selectedModule.color} data-id="element-466" />}
    </>;
}